import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { DOCTOR_MAPPINGS, STATE_DATA } from "../lib/doctorMappings";

const SITE_URL = process.env.REACT_APP_BACKEND_URL || "";

const SitemapDirectory = () => {
  return (
    <>
      <Helmet>
        <title>Doctor Directory - Find Any Specialist in Any State | NPI Lookup</title>
        <meta name="description" content="Browse our complete directory of doctors by specialty and state. Find cardiologists, dermatologists, pediatricians, and 24 more specialties across all 50 US states." />
        <link rel="canonical" href={`${SITE_URL}/directory`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Doctor Directory",
          "description": "Complete directory of healthcare providers by specialty and location",
          "url": `${SITE_URL}/directory`,
        })}</script>
      </Helmet>

      <div className="bg-white min-h-screen">
        {/* Hero */}
        <section className="bg-navy py-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="font-display font-bold text-3xl sm:text-4xl text-white mb-2" data-testid="directory-title">
              Doctor Directory
            </h1>
            <p className="text-slate-300 text-base max-w-2xl mx-auto">
              Browse {DOCTOR_MAPPINGS.length} medical specialties across all {STATE_DATA.length} US states.
              Every page links to verified, NPI-registered healthcare providers.
            </p>
          </div>
        </section>

        {/* Quick Stats */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto">
            <div className="text-center">
              <p className="font-display font-bold text-2xl text-navy">{DOCTOR_MAPPINGS.length}</p>
              <p className="text-xs text-slate-500">Specialties</p>
            </div>
            <div className="text-center">
              <p className="font-display font-bold text-2xl text-navy">{STATE_DATA.length}</p>
              <p className="text-xs text-slate-500">States</p>
            </div>
            <div className="text-center">
              <p className="font-display font-bold text-2xl text-navy">{DOCTOR_MAPPINGS.length * STATE_DATA.length}+</p>
              <p className="text-xs text-slate-500">Pages</p>
            </div>
          </div>
        </section>

        {/* Full Specialty × State Matrix */}
        {DOCTOR_MAPPINGS.map((m) => (
          <section key={m.url_slug} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8" id={m.url_slug}>
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="bg-slate-50 border-b border-slate-200 px-5 py-4 flex items-center justify-between">
                <div>
                  <h2 className="font-display font-semibold text-lg text-slate-900">
                    <Link to={`/find-a-doctor/${m.url_slug}`} className="hover:text-teal transition-colors">
                      {m.browse_label}
                    </Link>
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {m.official_name} &middot; {m.taxonomy_description} &middot; Also: {m.layman_terms.slice(0, 3).join(", ")}
                  </p>
                </div>
                <Link
                  to={`/find-a-doctor/${m.url_slug}`}
                  className="text-xs text-teal font-medium hover:underline shrink-0"
                >
                  View all &rarr;
                </Link>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-1.5">
                  {STATE_DATA.map((st) => (
                    <Link
                      key={st.code}
                      to={`/find-a-doctor/${m.url_slug}/${st.slug}`}
                      className="text-xs text-slate-600 bg-slate-50 hover:bg-teal/10 hover:text-teal rounded px-2 py-1.5 text-center transition-colors truncate"
                      title={`${m.browse_label} in ${st.name}`}
                    >
                      {st.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        ))}

        {/* Jump Links */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-5">
            <h2 className="font-display font-semibold text-base text-slate-900 mb-3">Quick Jump</h2>
            <div className="flex flex-wrap gap-2">
              {DOCTOR_MAPPINGS.map((m) => (
                <a
                  key={m.url_slug}
                  href={`#${m.url_slug}`}
                  className="text-xs bg-white border border-slate-200 rounded-full px-3 py-1 text-slate-600 hover:border-teal hover:text-teal transition-colors"
                >
                  {m.browse_label}
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Additional SEO Links */}
        <section className="bg-slate-50 py-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h3 className="font-display font-semibold text-sm text-slate-900 mb-3">Other Ways to Search</h3>
                <ul className="space-y-1.5">
                  <li><Link to="/find-a-doctor" className="text-sm text-slate-600 hover:text-teal">Find a Doctor (Plain English)</Link></li>
                  <li><Link to="/specialties" className="text-sm text-slate-600 hover:text-teal">Browse by Specialty</Link></li>
                  <li><Link to="/states" className="text-sm text-slate-600 hover:text-teal">Browse by State</Link></li>
                  <li><Link to="/near-me" className="text-sm text-slate-600 hover:text-teal">Find Doctors Near Me</Link></li>
                  <li><Link to="/dea-npi-lookup" className="text-sm text-slate-600 hover:text-teal">DEA Number Lookup</Link></li>
                </ul>
              </div>
              <div>
                <h3 className="font-display font-semibold text-sm text-slate-900 mb-3">Most Searched Specialties</h3>
                <ul className="space-y-1.5">
                  {DOCTOR_MAPPINGS.slice(0, 8).map((m) => (
                    <li key={m.url_slug}>
                      <Link to={`/find-a-doctor/${m.url_slug}`} className="text-sm text-slate-600 hover:text-teal">
                        {m.browse_label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-display font-semibold text-sm text-slate-900 mb-3">Largest States</h3>
                <ul className="space-y-1.5">
                  {["california", "texas", "florida", "new-york", "pennsylvania", "illinois", "ohio", "georgia"].map((slug) => {
                    const st = STATE_DATA.find(s => s.slug === slug);
                    return st ? (
                      <li key={st.code}>
                        <Link to={`/state/${st.code}`} className="text-sm text-slate-600 hover:text-teal">
                          Doctors in {st.name}
                        </Link>
                      </li>
                    ) : null;
                  })}
                </ul>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default SitemapDirectory;
